import { useEffect, useMemo, useState } from "react";
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  TextInput,
  Group,
  Button,
  Badge,
  Menu,
  ActionIcon,
  Stack,
  Select,
  Pagination,
  Avatar,
  Skeleton,
  Modal,
  Textarea,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconSearch,
  IconDotsVertical,
  IconCheck,
  IconX,
  IconRefresh,
  IconClock,
  IconMail,
  IconArrowsSort,
  IconArrowUp,
  IconArrowDown,
} from "@tabler/icons-react";
import { Endpoints } from "../../../lib/endpoints";
import { PaginatedResponse } from "../../../lib/types";
import { HttpCodes } from "../../../lib/data";
import { usePerformRequest, PerformRequest } from "../../../hooks/usePerformRequest";
import PermissionGuard from "../../../components/PermissionGuard";
import { AdminPermissions } from "../../../lib/data";
import {
  IWaitlist,
  WaitlistStatus,
  waitlistEntryId,
} from "../../../lib/types/Waitlist";

type SortColumn = "firstName" | "lastName" | "email" | "status" | "createdAt";
type SortDirection = "asc" | "desc";

const Waitlist = () => {
  useEffect(() => {
    document.title = "Waitlist | Virel Admin";
  }, []);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<WaitlistStatus | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortState, setSortState] = useState<{
    column: SortColumn;
    direction: SortDirection;
  }>({ column: "createdAt", direction: "desc" });

  const [actionProcessing, setActionProcessing] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedWaitlist, setSelectedWaitlist] = useState<IWaitlist | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
  const [approveTarget, setApproveTarget] = useState<IWaitlist | null>(null);

  const waitlistEndpoint = useMemo(
    () =>
      Endpoints.GetWaitlist({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
        status: statusFilter || undefined,
        sortBy: sortState.column,
        sortDir: sortState.direction,
      }),
    [currentPage, pageSize, debouncedSearch, statusFilter, sortState]
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, reloadData } = usePerformRequest<
    PaginatedResponse<IWaitlist[]>
  >({
    method: waitlistEndpoint.method,
    route: waitlistEndpoint.route,
    showError: false,
  });

  const toggleSort = (column: SortColumn) => {
    setSortState((prev) => {
      if (prev.column === column) {
        return {
          column,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      return { column, direction: "asc" };
    });
  };

  const sortIndicator = (column: SortColumn) => {
    if (sortState.column !== column) return <IconArrowsSort size={14} />;
    return sortState.direction === "asc" ? (
      <IconArrowUp size={14} />
    ) : (
      <IconArrowDown size={14} />
    );
  };

  const items = data?.data ?? [];
  const totalRecords = data?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil((totalRecords || 0) / pageSize));
  const rangeStart = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd =
    totalRecords === 0 ? 0 : Math.min(currentPage * pageSize, totalRecords);

  const getStatusBadge = (status: WaitlistStatus) => {
    const configs = {
      pending: {
        color: "yellow",
        label: "Pending",
        icon: <IconClock size={12} />,
      },
      approved: {
        color: "green",
        label: "Approved",
        icon: <IconCheck size={12} />,
      },
      rejected: { color: "red", label: "Rejected", icon: <IconX size={12} /> },
      invited: { color: "blue", label: "Invited", icon: <IconMail size={12} /> },
    };
    const config = configs[status] || configs.pending;
    return (
      <Badge
        color={config.color}
        variant="light"
        size="sm"
        leftSection={config.icon}
      >
        {config.label}
      </Badge>
    );
  };

  const handleApprove = async (item: IWaitlist) => {
    const entryId = waitlistEntryId(item);
    if (!entryId) {
      notifications.show({
        color: "red",
        title: "Invite failed",
        message: "Missing waitlist entry id",
      });
      return;
    }

    setActionProcessing(true);
    const endpoint = Endpoints.InviteWaitlist(entryId);
    const r = await PerformRequest<{
      message?: string;
      data?: {
        inviteEmailSent?: boolean;
        accountCreated?: boolean;
      };
    }>({
      method: endpoint.method,
      route: endpoint.route,
    });
    setActionProcessing(false);

    if (r.status === HttpCodes.Success || r.status === HttpCodes.Created) {
      const emailFailed = r.data?.data?.inviteEmailSent === false;
      notifications.show({
        color: emailFailed ? "yellow" : "green",
        title: "Invite complete",
        message:
          r.data?.message ||
          (emailFailed
            ? `${item.email} can sign in, but the invitation email failed`
            : `${item.email} invited — they can sign in to the app`),
      });
      await reloadData();
    } else {
      notifications.show({
        color: "red",
        title: "Invite failed",
        message: r.data?.message || r.message || "Could not invite this user",
      });
    }
  };

  const handleReject = async () => {
    if (!selectedWaitlist) return;
    const entryId = waitlistEntryId(selectedWaitlist);
    if (!entryId) return;

    setActionProcessing(true);
    const endpoint = Endpoints.UpdateWaitlist(entryId);
    const r = await PerformRequest<{ message?: string }>({
      method: endpoint.method,
      route: endpoint.route,
      data: {
        status: "rejected",
        reviewNotes: rejectReason,
      },
    });
    setActionProcessing(false);
    if (r.status === HttpCodes.Success) {
      notifications.show({
        color: "green",
        title: "Rejected",
        message: `${selectedWaitlist.email} was rejected`,
      });
      setRejectModalOpen(false);
      setRejectReason("");
      setSelectedWaitlist(null);
      await reloadData();
    } else {
      notifications.show({
        color: "red",
        title: "Reject failed",
        message: r.data?.message || r.message || "Could not reject this entry",
      });
    }
  };

  const formatDate = (value?: string | Date) => {
    if (!value) return "—";
    const date = new Date(value);
    return (
      date.toLocaleDateString() +
      " " +
      date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    );
  };

  const rows = items.map((item) => {
    const entryId = waitlistEntryId(item);
    return (
      <Table.Tr key={entryId}>
        <Table.Td>
          <Group gap="sm">
            <Avatar size="sm" color="brand" radius="sm">
              {item.firstName.charAt(0)}
            </Avatar>
            <div>
              <Text fw={500} size="sm">
                {item.firstName} {item.lastName}
              </Text>
              <Text size="xs" c="dimmed">
                {item.email}
              </Text>
            </div>
          </Group>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{item.email}</Text>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{item.phone}</Text>
        </Table.Td>
        <Table.Td>{getStatusBadge(item.status)}</Table.Td>
        <Table.Td>
          <Text size="sm">{formatDate(item.createdAt)}</Text>
        </Table.Td>
        <Table.Td>
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray">
                <IconDotsVertical size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {(item.status === "pending" || item.status === "approved") && (
                <>
                  <Menu.Item
                    leftSection={<IconCheck size={14} />}
                    color="green"
                    onClick={() => {
                      setApproveTarget(item);
                      setApproveConfirmOpen(true);
                    }}
                    disabled={actionProcessing}
                  >
                    Approve & Invite
                  </Menu.Item>
                  {item.status === "pending" && (
                    <Menu.Item
                      leftSection={<IconX size={14} />}
                      color="red"
                      onClick={() => {
                        setSelectedWaitlist(item);
                        setRejectModalOpen(true);
                      }}
                      disabled={actionProcessing}
                    >
                      Reject
                    </Menu.Item>
                  )}
                </>
              )}
              <Menu.Item
                leftSection={<IconRefresh size={14} />}
                onClick={() => reloadData()}
              >
                Refresh
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Table.Td>
      </Table.Tr>
    );
  });

  const skeletonRows = Array.from({ length: 5 }).map((_, idx) => (
    <Table.Tr key={`skeleton-${idx}`}>
      <Table.Td>
        <Skeleton height={32} width="100%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={12} width="80%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={12} width="60%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={20} width={70} />
      </Table.Td>
      <Table.Td>
        <Skeleton height={12} width="70%" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={24} width={24} />
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <PermissionGuard permission={AdminPermissions.MANAGE_USERS}>
      <Container size="xl">
        <Stack gap="lg">
          <Group justify="space-between">
            <div>
              <Title order={1}>Waitlist</Title>
              <Text size="sm" c="dimmed">
                Manage users waiting for access
              </Text>
            </div>
            <Button
              variant="light"
              leftSection={<IconRefresh size={16} />}
              onClick={() => reloadData()}
              loading={isLoading}
            >
              Reload
            </Button>
          </Group>

          <Paper shadow="sm" p="md" radius="md" withBorder>
            <Group gap="md" mb="md">
              <TextInput
                placeholder="Search by name, email, or phone..."
                leftSection={<IconSearch size={16} />}
                value={searchInput}
                onChange={(e) => setSearchInput(e.currentTarget.value)}
                style={{ flex: 1 }}
              />
              <Select
                placeholder="Filter by status"
                data={[
                  { value: "pending", label: "Pending" },
                  { value: "approved", label: "Approved" },
                  { value: "rejected", label: "Rejected" },
                  { value: "invited", label: "Invited" },
                ]}
                value={statusFilter}
                onChange={(val) => {
                  setStatusFilter(val as WaitlistStatus);
                  setCurrentPage(1);
                }}
                clearable
              />
            </Group>

            <div style={{ overflowX: "auto" }}>
              <Table striped highlightOnHover verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th
                      onClick={() => toggleSort("firstName")}
                      style={{ cursor: "pointer" }}
                    >
                      <Group gap={4}>User {sortIndicator("firstName")}</Group>
                    </Table.Th>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Phone</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th
                      onClick={() => toggleSort("createdAt")}
                      style={{ cursor: "pointer" }}
                    >
                      <Group gap={4}>
                        Signed Up {sortIndicator("createdAt")}
                      </Group>
                    </Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {isLoading ? (
                    skeletonRows
                  ) : rows.length > 0 ? (
                    rows
                  ) : (
                    <Table.Tr>
                      <Table.Td colSpan={6} align="center" py="xl">
                        <Text c="dimmed">No one found on the waitlist</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </div>

            <Group justify="space-between" mt="md">
              <Text size="sm" c="dimmed">
                Showing {rangeStart} to {rangeEnd} of {totalRecords} entries
              </Text>
              {totalPages > 1 && (
                <Pagination
                  value={currentPage}
                  onChange={setCurrentPage}
                  total={totalPages}
                />
              )}
            </Group>
          </Paper>
        </Stack>

        <Modal
          opened={rejectModalOpen}
          onClose={() => setRejectModalOpen(false)}
          title="Reject Waitlist Entry"
          centered
        >
          <Stack>
            <Text size="sm">
              Are you sure you want to reject {selectedWaitlist?.firstName}{" "}
              {selectedWaitlist?.lastName}?
            </Text>
            <Textarea
              label="Reason (Optional)"
              placeholder="Provide a reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.currentTarget.value)}
            />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => setRejectModalOpen(false)}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={handleReject}
                loading={actionProcessing}
              >
                Reject
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={approveConfirmOpen}
          onClose={() => setApproveConfirmOpen(false)}
          title="Confirm Approval"
          centered
          size="sm"
        >
          <Stack>
            <Text size="sm">
              Are you sure you want to approve and invite{" "}
              <Text span fw={700}>
                {approveTarget?.firstName} {approveTarget?.lastName}
              </Text>
              ?
            </Text>
            <Text size="xs" c="dimmed">
              This creates their account if needed, opens login access, and sends
              the VIREL invitation to {approveTarget?.email}.
            </Text>
            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => setApproveConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                color="green"
                leftSection={<IconCheck size={14} />}
                loading={actionProcessing}
                onClick={async () => {
                  if (approveTarget) {
                    await handleApprove(approveTarget);
                  }
                  setApproveConfirmOpen(false);
                  setApproveTarget(null);
                }}
              >
                Approve & Invite
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </PermissionGuard>
  );
};

export default Waitlist;
